<template>
    <div class="alert-container" :class="[variant, size]">
        <div v-if="variant" class="alert-icon">
            <glyph-icon v-if="variant === ComponentState.Default || variant === ComponentState.Timeout" :type="GlyphType.Info" />
            <glyph-icon v-if="variant === ComponentState.Success" :type="GlyphType.Success" />
            <glyph-icon v-if="variant === ComponentState.Critical || variant === ComponentState.Error" :type="GlyphType.Alert" />
        </div>
        <div class="alert-text">
            <slot />
        </div>
    </div>
</template>

<script setup>
import { GlyphIcon, GlyphType } from 'ihunter-pathfinder-components';
import { ComponentState, ComponentVariant } from '../composables/useConstants';

const props = defineProps({
    variant: {
        type: String,
        default: null,
    },
    size: {
        type: String,
        default: ComponentVariant.Box,
    },
});
</script>

<style lang="scss" scoped>
@use 'ihunter-pathfinder/styles' as Pathfinder;

.alert-container {
    display: flex;
    align-items: flex-start;
    gap: Pathfinder.$space-100;
    padding: Pathfinder.$space-200;
    border: solid 1px var(--surface-glowbox-border);
    background: var(--surface-glowbox-alt);
    border-radius: Pathfinder.$space-200;
    @include Pathfinder.Bodycopy;

    &.empty {
        display: none;
    }

    .alert-icon {
        height: 21px; // 21px height to match the font size of Bodycopy
        display: flex;
        justify-content: center;
        align-items: center;

        ::v-deep(svg) {
            display: flex;
            height: Pathfinder.$space-200;
            width: Pathfinder.$space-200;
            fill: var(--on-surface-glowbox);
        }

        &.empty {
            display: none;
        }
    }

    .alert-text {
        color: var(--on-surface-glowbox);
        flex-grow: 1;
        flex-shrink: 1;
        min-width: 0;
        white-space: wrap;
        word-break: break-word;
        overflow-wrap: break-word;
    }

    &.box {
        width: 100%;
    }

    &.inline,
    &.tucked {
        display: flex;
        width: fit-content;
        padding: Pathfinder.$space-25 Pathfinder.$space-100;
        border: none;
        @include Pathfinder.Subcopy;
        gap: Pathfinder.$space-75;
        //min-width: 0;
        // white-space: normal; /* Prevent wrapping */
        // word-break: break-word; /* Break long words */
        // overflow-wrap: break-word; /* Alternative for better compatibility */

        .alert-icon {
            height: 17px;

            ::v-deep(svg) {
                height: Pathfinder.$space-150;
                width: Pathfinder.$space-150;
            }
        }
    }

    &.tucked {
        padding: Pathfinder.$space-25 Pathfinder.$space-150;
        @include Pathfinder.Legalcopy;

        .alert-icon {
            height: 16px;
        }
    }

    &.success {
        border: solid 1px var(--success-glowbox-border);
        background: var(--success-glowbox);

        .alert-icon {
            ::v-deep(svg) {
                fill: var(--success);
            }
        }

        .alert-text {
            color: var(--on-success-glowbox);
        }

        &.inline,
        &.tucked {
            border: none;
        }
    }

    &.critical,
    &.error {
        border: solid 1px var(--critical-glowbox-border);
        background: var(--critical-glowbox);

        .alert-icon {
            ::v-deep(svg) {
                fill: var(--critical);
            }
        }

        .alert-text {
            color: var(--on-critical-glowbox);
        }

        &.inline,
        &.tucked {
            border: none;
        }
    }
}
</style>
